import React, { useCallback, useEffect } from "react";
import { Card, Row, Col, Button } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { setFilters } from "../incomeRecordSlice";
import { SearchOutlined } from "@ant-design/icons";
import RecordDateFilter from "../../../../components/filters/RecordDateFilter";
import SearchListAutoComplete from "../../../../components/SearchListAutoComplete";
import { fetchUsers } from "../../../../store/commonThunks/lookups";
import {
  fetchIncomeRecord,
  fetchIncomeAccountsBySearch,
} from "../incomeRecordThunk";
import { useDefaultDateRange } from "../../../../hooks/useDefaultDateRange";
import CommonDateRangeFilter from "../../../../components/common/filters/CommonDateRangeFilter";

const IncomeRecordFilters = ({ messageApi }) => {
  const dispatch = useDispatch();
  const { filters } = useSelector((state) => state.incomeRecord);

  const lookups = useSelector((state) => state.lookups);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const dateFrom = searchParams.get("dateFrom");
    const dateTo = searchParams.get("dateTo");

    if (dateFrom && dateTo) {
      const dateFilters = {
        filterType: "All",
        incId: null,
        userId: null,
        fromDate: dateFrom,
        toDate: dateTo,
      };
      dispatch(setFilters(dateFilters));
      dispatch(fetchIncomeRecord({ filters: { ...filters, ...dateFilters } }));
    }
  }, []);

  useDefaultDateRange(setFilters, filters, "today");

  const handleFilterChange = (field, value) => {
    dispatch({ type: "incomeRecord/resetList" });
    if (field === "filterType") {
      dispatch(setFilters({ incId: null, userId: null }));
    }
    dispatch(setFilters({ [field]: value }));
  };

  const searchIncome = useCallback(
    (text) => dispatch(fetchIncomeAccountsBySearch(text)),
    [dispatch]
  );

  useEffect(() => {
    if (filters.filterType === "By User") {
      dispatch(fetchUsers());
    }
  }, [dispatch, filters.filterType]);

  const handleSubmit = () => {
    if (!filters.filterType) {
      messageApi.warning("Select Filter Type");
      return;
    }
    dispatch(fetchIncomeRecord({ filters })).then((res) => {
      if (res.meta.requestStatus === "fulfilled") {
        messageApi.success("Report loaded ✅");
      } else {
        messageApi.error("Failed to fetch report ❌");
      }
    });
  };

  return (
    <Card className="mb-4 shadow-md rounded-xl">
      <Row gutter={[16, 16]} style={{ alignItems: "center" }}>
        {/* Filter Type */}
       <Col xs={24} sm={12} md={12} lg={8} xl={6}>
          <label>Filter Type</label>
          <SearchListAutoComplete
            optionsList={[
              { sale_id: "All", display_text: "All" },
              { sale_id: "By Income", display_text: "By Income" },
              { sale_id: "By User", display_text: "By User" },
            ]}
            value={
              filters.filterType
                ? { value: filters.filterType, label: filters.filterType }
                : undefined
            }
            onSelect={(obj) => {
              if(!obj) {
                handleFilterChange("filterType", null);
                return;
              } 
              handleFilterChange("filterType", obj?.sale_id);
            }}
            placeholder="Filter Type"
            showAddNew={false}
          />
        </Col>

        {/* Income Filter */}
        {filters.filterType === "By Income" && (
         <Col xs={24} sm={12} md={12} lg={8} xl={6}>
            <label>Search Income</label>
            <SearchListAutoComplete
              entityType="income"
              searchThunk={searchIncome}
              resultSelector={(state) => state.incomeRecord.incomeAccounts}
              onSelect={(obj) => handleFilterChange("incId", obj?.sale_id)}
              placeholder="Income"
              showAddNew={false}
              allowClear={true}
            />
          </Col>
        )}

        {/* User Filter */}
        {filters.filterType === "By User" && (
         <Col xs={24} sm={12} md={12} lg={8} xl={6}>
            <label>User</label>
            <SearchListAutoComplete
              entityType="user"
              optionsList={lookups.users.map((e) => ({
                sale_id: e.id,
                display_text: e.full_name,
              }))}
              onSelect={(obj) => handleFilterChange("userId", obj?.sale_id)}
              placeholder="User"
              allowClear={true}
            />
          </Col>
        )}

        {/* Record Type */}
        {filters.filterType !== "By Income" && (
          <Col xs={24} sm={12} md={12} lg={8} xl={6}>
            <label>Record Type</label>
            <SearchListAutoComplete
              optionsList={[
                { sale_id: "without details", display_text: "Without Details" },
                { sale_id: "with details", display_text: "With Details" },
              ]}
              value={
              filters.recordType
                ? { value: filters.recordType, label: filters.recordType }
                : undefined
            }
              onSelect={(obj) => {
              if(!obj) {
                handleFilterChange("recordType", null);
                return;
              } 
              handleFilterChange("recordType", obj?.sale_id);
            }}
              placeholder="Record Type"
              showAddNew={false}
            />
          </Col>
        )}

        {/* Date Range */}
        <CommonDateRangeFilter
        filters={filters}
        onChange={(fromDate, toDate) => {
        handleFilterChange("fromDate", fromDate);
        handleFilterChange("toDate", toDate);
        }}
      />

        {/* Submit */}
        <Col
          xs={24}
          sm={12}
          md={6}
          lg={4}
          style={{ display: "flex", alignItems: "flex-end", height: 58 }}
        >
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={handleSubmit}
            style={{height: 35.19}}
            block
          >
            Report
          </Button>
        </Col>
      </Row>
    </Card>
  );
};

export default IncomeRecordFilters;
